/**
 * @prettier
 */
import React, { FunctionComponent, useEffect, useState } from "react";
import { Paper, Grid, Box } from "@material-ui/core";
import moment from "moment";

import { firestore } from "firebase-tools/firebase";
import { PageType } from "types/PageType";

// The backend Spotify scraper writes its health to this doc on every run
// (see bemeasured-functions: src/spotifyMetrics/helpers/status.ts)
const STATUS_DOC = firestore
  .collection("spotify-metrics")
  .doc("scraper-status");

const COLORS: Record<string, string> = {
  green: "#2e7d32",
  amber: "#ef6c00",
  red: "#c62828",
  grey: "#616161",
};

const toDate = (value: any): Date | null => {
  if (!value) return null;
  if (typeof value.toDate === "function") return value.toDate();
  if (value._seconds) return new Date(value._seconds * 1000);
  return null;
};

const formatWhen = (date: Date | null): string =>
  date
    ? `${moment(date).format("ddd D MMM YYYY, HH:mm")} (${moment(
        date
      ).fromNow()})`
    : "never";

export const SpotifyStatusPage: FunctionComponent<PageType> = () => {
  const [status, setStatus] = useState<any>(undefined);
  const [loadError, setLoadError] = useState("");

  useEffect(() => {
    const unsubscribe = STATUS_DOC.onSnapshot(
      (snap) => setStatus(snap.exists ? snap.data() : null),
      (error) => setLoadError(error.message)
    );
    return unsubscribe;
  }, []);

  const updatedAt = toDate(status?.updated_at);
  const lastAuthOkAt = toDate(status?.lastAuthOkAt);
  const lastScrapeSuccessAt = toDate(status?.lastScrapeSuccessAt);
  const lastAuthFailAt = toDate(status?.lastAuthFailAt);

  let level = "grey";
  let headline = "Loading…";
  let detail = "";

  if (loadError) {
    level = "grey";
    headline = "Could not load status";
    detail = loadError;
  } else if (status === null) {
    level = "grey";
    headline = "No status reported yet";
    detail =
      "The scraper reports here on every run (every 2 hours). If this never changes, the upgraded Spotify functions may not be deployed yet.";
  } else if (status) {
    const hoursSinceReport = updatedAt
      ? moment().diff(moment(updatedAt), "hours")
      : null;
    const hoursSinceSuccess = lastScrapeSuccessAt
      ? moment().diff(moment(lastScrapeSuccessAt), "hours")
      : null;

    if (status.needsCookieRefresh || status.status === "auth_failed") {
      level = "red";
      headline = "Spotify login needs refreshing";
      detail =
        "The saved Spotify session has expired and automatic login failed. Artist stats are NOT being collected. Follow the refresh steps below.";
    } else if (hoursSinceReport !== null && hoursSinceReport > 6) {
      level = "amber";
      headline = "Scraper has stopped reporting";
      detail = `Nothing heard from the scraper for ${hoursSinceReport} hours (it runs every 2 hours). The functions may be failing before reaching Spotify — check the Cloud Function logs.`;
    } else if (hoursSinceSuccess !== null && hoursSinceSuccess > 48) {
      level = "amber";
      headline = "Login works but scraping is failing";
      detail =
        "The scraper is running and logged in, but no artist has been scraped successfully in over 2 days. Spotify may have changed their site again.";
    } else {
      level = "green";
      headline = "Healthy";
      detail = "Logged in and collecting artist stats normally.";
    }
  }

  return (
    <Box style={{ maxWidth: 900, margin: "0 auto", padding: "90px 20px 60px" }}>
      <h1 style={{ fontWeight: 900 }}>Spotify Scraper Status</h1>

      <Paper
        style={{
          padding: 24,
          borderLeft: `10px solid ${COLORS[level]}`,
          marginBottom: 24,
        }}
      >
        <h2 style={{ color: COLORS[level], margin: "0 0 8px" }}>{headline}</h2>
        <p style={{ margin: 0, fontSize: 16 }}>{detail}</p>
        {status?.lastError && (
          <p style={{ marginTop: 12, color: COLORS.red, fontSize: 14 }}>
            Last error: {status.lastError}
          </p>
        )}
      </Paper>

      {status && (
        <Paper style={{ padding: 24, marginBottom: 24 }}>
          <h3 style={{ marginTop: 0 }}>Details</h3>
          <Grid container spacing={1} style={{ fontSize: 15 }}>
            <Grid item xs={5}>
              Last report from scraper
            </Grid>
            <Grid item xs={7}>
              {formatWhen(updatedAt)}
            </Grid>
            <Grid item xs={5}>
              Last successful Spotify login
            </Grid>
            <Grid item xs={7}>
              {formatWhen(lastAuthOkAt)}
              {status.authSource ? ` — via ${status.authSource}` : ""}
            </Grid>
            <Grid item xs={5}>
              Last successful artist scrape
            </Grid>
            <Grid item xs={7}>
              {formatWhen(lastScrapeSuccessAt)}
              {status.lastScrapedArtist
                ? ` (artist ${status.lastScrapedArtist})`
                : ""}
            </Grid>
            {lastAuthFailAt && (
              <>
                <Grid item xs={5}>
                  Last login failure
                </Grid>
                <Grid item xs={7}>
                  {formatWhen(lastAuthFailAt)}
                </Grid>
              </>
            )}
          </Grid>
        </Paper>
      )}

      <Paper style={{ padding: 24 }}>
        <h3 style={{ marginTop: 0 }}>How the Spotify connection works</h3>
        <p>
          Artist stats are collected from Spotify for Artists every 2 hours
          using the <strong>spotify@be-hookd.com</strong> account. The backend
          carries a saved browser session (a &ldquo;cookie jar&rdquo;) so it
          never has to log in by itself. Spotify sessions last roughly a year,
          so expect to refresh about once a year — or sooner if Spotify forces a
          logout.
        </p>
        <h3>When this page says the login needs refreshing</h3>
        <ol style={{ lineHeight: 1.8 }}>
          <li>
            On the ops machine, run the Spotify cookie-refresh script (see the
            Spotify section of <code>RESURRECTION_PLAN.md</code> in the handover
            docs). A Chrome window opens and logs in as spotify@be-hookd.com —
            complete the captcha if one appears.
          </li>
          <li>
            Copy the exported cookie file into the backend repo at{" "}
            <code>
              functions/src/spotifyMetrics/helpers/cookies/spotify@be-hookd.com.json
            </code>
            .
          </li>
          <li>
            Redeploy the Spotify functions (
            <code>firebase deploy --only functions:spotify</code>
            ). The build bundles the new session automatically.
          </li>
          <li>
            This page should turn green within 2 hours (the next scheduled run).
          </li>
        </ol>
        <p>
          Technical contact: Peter Neill —{" "}
          <a href="mailto:peter@shootthesound.com">peter@shootthesound.com</a>
        </p>
      </Paper>
    </Box>
  );
};
